import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { useAtomValue } from "jotai";
import { ExpertModeApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { handleReactQueryErrors } from "@/helpers/errors";
import { useToasts } from "@/hooks/useToasts";
import { currentWorkspaceAtom } from "@/store";
import {
  type UseAcceptBriefBody,
  type UseGetProductsByClientParams,
  type UseGetTechnicalFieldsParams,
  type UseQualifyBriefBody,
  type UseSendBriefBody,
} from "./useExpertMode.types";

const apiClient = createApiClient(ExpertModeApi);

export const useExpertMode = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const { t } = useTranslate();

  const toaster = useToasts();

  const useSendBrief = () => {
    return useMutation({
      mutationKey: [
        "sendBrief",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      mutationFn: ({ submitBriefDto }: UseSendBriefBody) =>
        apiClient.expertModeControllerSendBrief({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          submitBriefDto,
        }),
      onError: (error) =>
        handleReactQueryErrors(error, (customError) => {
          if ("status" in customError) {
            if (customError.status === 409) {
              toaster.add({
                state: "error",
                title: t(
                  "order.mediumDetails.stepConfiguration.duplicateOrderNameError"
                ),
              });
              return true;
            }
          }
        }),
    });
  };

  // Brokers logic

  const useGetProductsByClient = ({
    language,
  }: UseGetProductsByClientParams) => {
    return useQuery({
      queryKey: [
        "productsByClient",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
        language,
      ],
      enabled: !!currentWorkspace && !!language,
      queryFn: () => {
        return apiClient.expertModeControllerGetProductsByClient({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          language,
        });
      },
    });
  };

  const useGetTechnicalFields = ({
    admaId,
    awsVersion,
  }: UseGetTechnicalFieldsParams) => {
    return useQuery({
      queryKey: [
        "getFields",
        admaId,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      enabled: !!admaId && awsVersion !== undefined,
      queryFn: () => {
        return apiClient.expertModeControllerGetProductFields({
          admaId,
          awsVersion,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
        });
      },
    });
  };

  const useQualifyBrief = () => {
    return useMutation({
      mutationKey: [
        "qualifyBrief",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      mutationFn: ({ orderItemId, ...qualifyBriefDto }: UseQualifyBriefBody) =>
        apiClient.expertModeControllerQualifyBrief({
          qualifyBriefDto,
          orderItemId,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
        }),
    });
  };

  const useAcceptBrief = () => {
    return useMutation({
      mutationKey: [
        "acceptBrief",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      mutationFn: ({ orderItemId }: UseAcceptBriefBody) =>
        apiClient.expertModeControllerAcceptOrderItem({
          orderItemId,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
        }),
    });
  };

  return {
    useSendBrief,
    useGetProductsByClient,
    useGetTechnicalFields,
    useQualifyBrief,
    useAcceptBrief,
  };
};
